import { Box, Skeleton, Stack } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";

import { Schedule } from "../../../components/schedule";
import { useSessionContext } from "../../../contexts/SessionContext";
import { GetConsents } from "../../../services/consent";
import { GetAppointment } from "../../../services/fhir";
import { DateFilterType } from "../../../utils/interfaces";
import { useDatetime } from "../../../utils/useDatetime";
import { fetchDependentAppointments } from "../../../utils/patient/fetchDependentAppointments";

interface ISelectOption {
  label: string;
  value: string;
}

export interface IFormInput {
  healthcareService: ISelectOption | undefined;
  organization: ISelectOption | undefined;
  practitioner: ISelectOption | undefined;
}

export function ScheduleForDash() {
  const [received, setReceived] = useState<any>([]);

  const { inviteDialog, user, patientRelated, access } = useSessionContext();
  const { formatDateToISO, isDateFilterTypeDay, isSameYearAndMonthAsToday } =
    useDatetime;

  const [appointments, setAppointments] = useState<any>([]);

  const [appointmentsDepedents, setAppointmentsDependents] = useState<any>([]);
  const [dependentes, setDependentes] = useState<any>([]);
  const [dateFilter, setDateFilter] = useState<Dayjs>(dayjs());
  const [filterType, setFilterType] = useState<DateFilterType>("month");
  const [loading, setLoading] = useState<boolean>(false);
  const [refetch, setRefetch] = useState<boolean>(false);

  useEffect(() => {
    async function fetchAppointments() {
      setLoading(true);

      // Calcula a data de início baseado no tipo de filtro
      const startDate =
        isDateFilterTypeDay(filterType) || isSameYearAndMonthAsToday(dateFilter)
          ? formatDateToISO(dateFilter)
          : filterType === "week"
            ? formatDateToISO(dateFilter.startOf("week"))
            : formatDateToISO(dateFilter.startOf("month"));

      // Calcula a data de fim baseado no tipo de filtro
      const endDate = isDateFilterTypeDay(filterType)
        ? formatDateToISO(dateFilter)
        : filterType === "week"
          ? formatDateToISO(dateFilter.endOf("week"))
          : formatDateToISO(dateFilter.endOf("month"));

      const response = await GetAppointment({
        ehrrunner: `${user.username}`,
        startDate,
        endDate,
        status: ["booked", "pending", "proposed"],
        sort: "date",
        includeLocation: true,
      });

      setAppointments(response);
      setLoading(false);
    }
    fetchAppointments();
  }, [
    dateFilter,
    filterType,
    user.username,
    formatDateToISO,
    isDateFilterTypeDay,
    isSameYearAndMonthAsToday,
    refetch,
  ]);

  useEffect(() => {
    async function fetchData() {
      try {
        const receivedResponse = await GetConsents({
          identifier: user.username,
          type: "received",
        });
        setReceived(
          receivedResponse?.data
            ?.filter(
              (el: any) =>
                el?.recurso?.id !== user.username && el?.recurso?.nome,
            )
            ?.map((el: any) => ({
              id: el?.recurso?.id,
              purpose: el?.contexto?.motivoAcesso,
            })),
        );
      } catch (err) {
        console.log("Fetch consents", err);
      } finally {
      }
    }
    fetchData();
  }, [user.username]);

  useEffect(() => {
    const auxDependents = patientRelated?.filter((el: any) => el?.isVerify);
    if (auxDependents?.length) setDependentes(auxDependents);
    async function fetchAppointmentsDependentes() {
      setLoading(true);

      // Calcula a data de início baseado no tipo de filtro
      const startDate =
        isDateFilterTypeDay(filterType) || isSameYearAndMonthAsToday(dateFilter)
          ? formatDateToISO(dateFilter)
          : filterType === "week"
            ? formatDateToISO(dateFilter.startOf("week"))
            : formatDateToISO(dateFilter.startOf("month"));

      // Calcula a data de fim baseado no tipo de filtro
      const endDate = isDateFilterTypeDay(filterType)
        ? formatDateToISO(dateFilter)
        : filterType === "week"
          ? formatDateToISO(dateFilter.endOf("week"))
          : formatDateToISO(dateFilter.endOf("month"));

      // Centraliza consentimento + fetch de appointments dos dependentes.
      const allAppointments = await fetchDependentAppointments({
        dependents: auxDependents,
        receivedConsents: received,
        startDate,
        endDate,
      });

      setAppointmentsDependents(allAppointments);
      setLoading(false);
    }

    if (auxDependents?.length && received?.length)
      fetchAppointmentsDependentes();
  }, [
    dateFilter,
    filterType,
    user.username,
    formatDateToISO,
    isDateFilterTypeDay,
    isSameYearAndMonthAsToday,
    patientRelated,
    received,
    refetch,
  ]);

  if (inviteDialog) {
    return <Skeleton variant="rectangular" width="100%" height="15vh" />;
  }

  return (
    <>
      <Stack
        direction={{ xs: "column", lg: "row" }}
        gap="24px"
        height={{
          xs: "200px",
          sm: "400px",
          lg: access?.type === "patient" ? " 60vh" : "70vh",
        }}
        // minHeight="70vh"
      >
        <Box
          sx={{
            width: { xs: "100%", lg: "100%" },
            height: "100%",
            borderRadius: { xs: "8px", md: "16px" },
            mb: 2,
            padding: { xs: "16px", md: "24px 40px" },
          }}
        >
          <Schedule
            relatedPatient={dependentes}
            bundle={{
              ...appointments,
              entry: [
                ...(appointments?.entry || []), // Verifica se appointments.entry é um array válido, senão usa um array vazio
                ...(appointmentsDepedents || []), // Verifica se appointmentsDepedents é um array válido, senão usa um array vazio
              ],
            }}
            dateFilter={dateFilter}
            filterType={filterType}
            setDateFilter={setDateFilter}
            setFilterType={setFilterType}
            loading={loading}
            setRefetch={setRefetch}
            refetch={refetch}
          />
        </Box>
      </Stack>
    </>
  );
}
